import { EyeOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown } from 'antd';
import React from 'react';
import {
  AGGREGATE_FUNCTION_LABELS,
  AGGREGATE_FUNCTIONS,
  FIELD_TYPES,
  REGISTER_CALCULATION_TYPES,
} from '../common/constants';

export const createAggregateHandlers = (
  selectedAggregates,
  setSelectedAggregates,
  setAggregateResults,
) => {
  const handleAggregateChange = (
    fieldKey,
    checkedValues,
    getRegisterStats,
    registerId,
    registerDetailsType,
    registerStatsFilter = {},
  ) => {
    const updatedState = { ...selectedAggregates };

    if (checkedValues?.length > 0) {
      updatedState[fieldKey] = checkedValues;
    } else {
      delete updatedState[fieldKey];
    }

    setSelectedAggregates(updatedState);

    if (Object?.keys(updatedState)?.length > 0) {
      const aggregateData = Object?.entries(updatedState)?.map(
        ([fieldId, operations]) => ({
          id: fieldId,
          operations,
        }),
      );

      const filter =
        typeof registerStatsFilter === 'string' && registerStatsFilter
          ? { registerLogId: registerStatsFilter }
          : registerStatsFilter;

      getRegisterStats({
        variables: {
          data: {
            fields: aggregateData,
            registerId,
            type: registerDetailsType,
          },
          filter,
        },
      });
    } else {
      setAggregateResults({});
    }
  };

  const handleSelectAll = (
    key,
    getRegisterStats,
    registerId,
    registerDetailsType,
    registerStatsFilter = {},
  ) => {
    const allOperations = [
      AGGREGATE_FUNCTIONS.TOTAL,
      AGGREGATE_FUNCTIONS.AVERAGE,
      AGGREGATE_FUNCTIONS.MEDIAN,
      AGGREGATE_FUNCTIONS.STANDARD_DEVIATION,
    ];

    const updatedState = {
      ...selectedAggregates,
      [key]: allOperations,
    };

    setSelectedAggregates(updatedState);

    const aggregateData = Object?.entries(updatedState)?.map(
      ([fieldId, operations]) => ({
        id: fieldId,
        operations,
      }),
    );

    const filter =
      typeof registerStatsFilter === 'string' && registerStatsFilter
        ? { registerLogId: registerStatsFilter }
        : registerStatsFilter;

    getRegisterStats({
      variables: {
        data: {
          fields: aggregateData,
          registerId,
          type: registerDetailsType,
        },
        filter,
      },
    });
  };

  const handleClearAll = (
    key,
    getRegisterStats,
    registerId,
    registerDetailsType,
    registerStatsFilter = {},
  ) => {
    const updatedState = { ...selectedAggregates };
    delete updatedState?.[key];
    setSelectedAggregates(updatedState);

    if (Object?.keys(updatedState)?.length > 0) {
      const aggregateData = Object?.entries(updatedState)?.map(
        ([fieldId, operations]) => ({
          id: fieldId,
          operations,
        }),
      );

      const filter =
        typeof registerStatsFilter === 'string' && registerStatsFilter
          ? { registerLogId: registerStatsFilter }
          : registerStatsFilter;

      getRegisterStats({
        variables: {
          data: {
            fields: aggregateData,
            registerId,
            type: registerDetailsType,
          },
          filter,
        },
      });
    } else {
      setAggregateResults({});
    }
  };

  return {
    handleAggregateChange,
    handleSelectAll,
    handleClearAll,
  };
};

export const processRegisterStatsResults = (res, setAggregateResults) => {
  const fieldStats = res?.registerStats?.fieldStats || [];
  const results = {};
  fieldStats?.forEach((field) => {
    results[field?.id] = {};
    field?.operations?.forEach((op) => {
      results[field?.id][op?.operation] = op?.result;
    });
  });
  setAggregateResults(results);
};

const AggregateDropdown = ({
  fieldKey,
  fieldType,
  calculationResultType,
  selectedAggregates,
  onAggregateChange,
  onSelectAll,
  onClearAll,
  hasData = true,
}) => {
  if (
    fieldType !== FIELD_TYPES.NUMERIC &&
    calculationResultType !== REGISTER_CALCULATION_TYPES.NUMERIC
  ) {
    return null;
  }

  const aggregateOptions = [
    {
      label: AGGREGATE_FUNCTION_LABELS[AGGREGATE_FUNCTIONS.TOTAL],
      value: AGGREGATE_FUNCTIONS.TOTAL,
    },
    {
      label: AGGREGATE_FUNCTION_LABELS[AGGREGATE_FUNCTIONS.AVERAGE],
      value: AGGREGATE_FUNCTIONS.AVERAGE,
    },
    {
      label: AGGREGATE_FUNCTION_LABELS[AGGREGATE_FUNCTIONS.MEDIAN],
      value: AGGREGATE_FUNCTIONS.MEDIAN,
    },
    {
      label: AGGREGATE_FUNCTION_LABELS[AGGREGATE_FUNCTIONS.STANDARD_DEVIATION],
      value: AGGREGATE_FUNCTIONS.STANDARD_DEVIATION,
    },
  ];

  const selectedValues = selectedAggregates?.[fieldKey] || [];
  const totalOptions = aggregateOptions?.length;
  const selectedCount = selectedValues?.length;

  const renderButtons = () => {
    if (selectedCount === 0) {
      return (
        <div className="d-flex align-center justify-center p-5">
          <Button
            type="primary"
            size="small"
            className="width-percent-100"
            onClick={(e) => {
              e?.stopPropagation();
              onSelectAll(fieldKey);
            }}
            disabled={!hasData}
          >
            Select All
          </Button>
        </div>
      );
    }
    if (selectedCount === totalOptions) {
      return (
        <div className="d-flex align-center justify-center p-5">
          <Button
            size="small"
            className="width-percent-100"
            onClick={(e) => {
              e?.stopPropagation();
              onClearAll(fieldKey);
            }}
            disabled={!hasData}
          >
            Deselect All
          </Button>
        </div>
      );
    }
    return (
      <div className="d-flex align-center justify-between gap-4 p-5">
        <Button
          type="primary"
          size="small"
          className="width-percent-100"
          onClick={(e) => {
            e?.stopPropagation();
            onSelectAll(fieldKey);
          }}
          disabled={!hasData}
        >
          Select All
        </Button>
        <Button
          size="small"
          className="width-percent-100"
          onClick={(e) => {
            e?.stopPropagation();
            onClearAll(fieldKey);
          }}
          disabled={!hasData}
        >
          Deselect All
        </Button>
      </div>
    );
  };

  const renderAggregateOptions = (
    <div
      className="aggregate-functions-container"
      onClick={(e) => e?.stopPropagation()}
    >
      <div className="d-flex fw-medium p-5">
        Aggregate Functions
        {!hasData && (
          <div className="danger-text ml-5">(No data available)</div>
        )}
      </div>
      {renderButtons()}
      <div className="d-flex flex-vertical p-5">
        <Checkbox.Group
          className="d-flex flex-vertical"
          options={aggregateOptions}
          value={selectedAggregates?.[fieldKey] || []}
          onChange={(checkedValues) =>
            onAggregateChange(fieldKey, checkedValues)
          }
          disabled={!hasData}
        />
      </div>
    </div>
  );

  return (
    <Dropdown
      popupRender={() => renderAggregateOptions}
      trigger={['click']}
      placement="bottomLeft"
    >
      <EyeOutlined
        className="ml-5 cursor-pointer text-primary"
        onClick={(e) => e?.stopPropagation()}
      />
    </Dropdown>
  );
};

export default AggregateDropdown;
